import type { Message, RealtimeChannel } from "ably";
import { useCallback, useMemo } from "react";
import { PeerConnectionError } from "@/hooks/usePeerConnectionErrors";
import { useStore } from "@/store";
import type { LastMessagePollingConfig, TypedChannel } from "../../core";
import { useChannel, useLastMessageAction as useGenericLastMessageAction, useSafePublish } from "../../react";
import type { DirectHistoryFetcher } from "../directHistory";
import type { MessagePayload, PeerMessage, PeerMessageType } from "../messaging";
import { visionAblyChannelName } from "../messaging";
import { useVisionPresence } from "./useVisionPresence";

export type TypedAblyChannel = TypedChannel<{ [K in PeerMessageType]: MessagePayload<K> }>;

export function useVisionChannel(id: string | null, onMessage?: (message: PeerMessage) => void) {
	const channelName = visionAblyChannelName(id);
	const { channel } = useChannel<TypedAblyChannel>(channelName, onMessage as (message: unknown) => void);
	const connectionStatus = useVisionPresence(channelName);
	const createConnectionError = useCallback(() => new PeerConnectionError(), []);
	const { publish } = useSafePublish(
		channel as unknown as RealtimeChannel | null,
		{
			isConnected: connectionStatus.isEvaluatedDeviceConnected && connectionStatus.isScannerDeviceConnected,
			isReady: connectionStatus.isInitialCheckComplete,
		},
		createConnectionError,
	);

	const safePublish = publish as <K extends PeerMessageType>(name: K, data: MessagePayload<K>) => Promise<void>;

	return { channel, channelName, connectionStatus, safePublish };
}

type UseLastMessageActionOptions = {
	pollingConfig?: LastMessagePollingConfig | null;
	directFetcher?: DirectHistoryFetcher | null;
};

export function useLastMessageAction(
	ablyChannel: TypedAblyChannel | null,
	onLastMessage: (msg: PeerMessage) => void,
	options?: UseLastMessageActionOptions,
) {
	const initialFetchDelayMs = useStore((state) => state.globalConfig?.realtimeHistory?.initialFetchDelayMs);
	const pollIntervalMs = useStore((state) => state.globalConfig?.realtimeHistory?.pollIntervalMs);

	const defaultPollingConfig = useMemo<LastMessagePollingConfig | null>(() => {
		if (initialFetchDelayMs && initialFetchDelayMs > 0 && pollIntervalMs && pollIntervalMs > 0) {
			return { initialFetchDelayMs, pollIntervalMs };
		}
		return null;
	}, [initialFetchDelayMs, pollIntervalMs]);

	useGenericLastMessageAction(
		ablyChannel as unknown as RealtimeChannel | null,
		onLastMessage as (msg: Message) => void,
		options?.pollingConfig ?? defaultPollingConfig,
		options?.directFetcher,
	);
}
